import React, { useCallback, useEffect, useMemo } from 'react';

import type { Property } from '@ts-types/property';

import { getDroneCheckout, getDroneCouponDiscount } from '@vb-django/api/services/drone';
import { useLogin } from '@vb-django/components/login/context/LoginContext';
import { usePropertyFetcher } from '@vb-django/components/minskog/hooks/usePropertyFetcher';
import { usePropsDataStore } from '@vb-django/components/minskog/store/PropsDataStore';

import { Chip, Icon, Text } from '@ui-component-library';

import {
  CheckoutConfig,
  ConfirmProductSelectionFn,
  CouponDiscount,
  OpenCheckoutOptions,
  Product
} from '@components/Checkout/Checkout.types';
import type { PaymentFetchResponse } from '@components/Checkout/Checkout.types';
import { mapPropertiesData } from '@components/Checkout/config/utils/utils';
import { CheckoutProvider } from '@components/Checkout/context/CheckoutContext';
import { getStandardCheckoutStepKeys } from '@components/Checkout/utils/standardCheckoutStepKeys';

import { DroneCheckoutInner } from './components/DroneCheckoutInner';
import { DroneInfo } from './components/DroneInfo';
import { ObstacleInfo } from './components/ObstacleInfo';
import { PackageSummary } from './components/PackageSummary';

export default function DroneCheckout({
  isOpen,
  onClose,
  openOptions,
  onConsumedOpenOptions
}: {
  isOpen?: boolean;
  onClose: () => void;
  openOptions?: OpenCheckoutOptions | null;
  onConsumedOpenOptions?: () => void;
}) {
  const { isLogged } = useLogin() ?? { isLogged: false };
  const { props, setOneProp } = usePropsDataStore();
  const { fetchPropData } = usePropertyFetcher();

  const initialProducts = useMemo(() => {
    if (!isOpen) {
      return [] as Product[];
    }
    const mappedProperties = mapPropertiesData(props);
    return mappedProperties.map((property) => ({
      id: property.ref,
      type: property.type,
      name: property.designation,
      description: property.area ? `${Math.round(property.area)} hektar` : 'Hämtar data...',
      price: 0,
      area: property.area ?? 0,
      lmID: property.lmID ?? null,
      disabled: property.disabled ?? false,
      isShared: property.isShared ?? false,
      dfr: property.dfr ?? false
    }));
  }, [isOpen, props]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    props.forEach((prop: Property) => {
      const area = prop.area ?? prop.data?.area;
      if ((area === undefined || area === null) && !prop.fetch && !prop.ready) {
        fetchPropData(setOneProp, prop);
      }
    });
  }, [isOpen, props, fetchPropData, setOneProp]);

  const stepKeys = useMemo(() => getStandardCheckoutStepKeys(isLogged), [isLogged]);

  const confirmDroneProductSelection = useCallback<ConfirmProductSelectionFn>(
    async ({ pendingSelected, userProducts }) => {
      const nextSelected = pendingSelected.map((pendingProduct) => {
        const full = userProducts.find((userProduct) => userProduct.id === pendingProduct.id);
        return full ?? pendingProduct;
      });
      return { nextSelected };
    },
    []
  );

  const config = useMemo<CheckoutConfig>(
    () => ({
      type: 'drone',
      title: 'Beställ drönarflygning',
      subtitle: 'Utöver Ortofoto välj eventuell skadeanalys som tillägg',
      summaryProductLabel: 'Drönarflygning',
      selectProductLabel: 'Inga fastigheter valda',
      selectDialogTitle: 'Välj fastighet',
      stepKeys,
      productContent: <DroneInfo />,
      termsContent: <ObstacleInfo />,
      summaryAdditionalContent: <PackageSummary />,
      isOptionDisabled: (option) =>
        Boolean(option.disabled) || Boolean(option.isShared) || Boolean(!option.dfr),
      renderOptionBadge: (option) => (
        <div className="flex flex-row items-center gap-1">
          <>
            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
              Tillagd
            </Text>
            <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
            {option.dfr ? (
              <Chip
                label="Plus"
                className="bg-[#F6F5FE] fill-[#6856C3] text-[#6856C3]"
                icon="SparkleFilled"
              />
            ) : (
              <Chip label="Saknar Plus" className="bg-[#F4F5F5] text-[#30353A]" icon={null} />
            )}
          </>
        </div>
      ),
      fetchCouponDiscount: async (code, products, extraData): Promise<CouponDiscount> => {
        const pkg = extraData.selectedPackage;
        const fmp = extraData.selectedForestManagementPlan;
        const { ok, data } = await getDroneCouponDiscount({
          refs: products.map((product) => product.id),
          package_id: pkg?.id,
          forest_management_plan_id: fmp?.id,
          coupon_code: code
        });
        if (!ok || !data) {
          throw new Error('Ogiltig rabattkod');
        }
        const discount: CouponDiscount = {
          coupon_discount_price: data.coupon_discount_price,
          coupon_discount_percentage: data.coupon_discount_percentage,
          coupon_code: code
        };
        return discount;
      },
      fetchCheckout: (selectedProducts, couponDiscount, extraData) => {
        const pkg = extraData.selectedPackage;
        const fmp = extraData.selectedForestManagementPlan;
        return getDroneCheckout({
          refs: selectedProducts.map((product) => product.id),
          package_id: pkg?.id,
          fmp_id: fmp?.id,
          coupon_code: couponDiscount?.coupon_code,
          extra_data: {
            has_obstacles: extraData.hasObstacles ?? false,
            obstacle_description: extraData.obstacleDescription ?? undefined,
            location_terms: extraData.locationTerms === true,
            ownership_terms: extraData.ownershipTerms === true
          }
        }) as Promise<PaymentFetchResponse>;
      },
      validateOrderStep: ({ extraData }) => {
        const errors: { [key: string]: string } = {};
        if (extraData.hasObstacles === undefined) {
          errors.obstacleInfo = 'Du måste ange om det finns hinder';
        }
        if (extraData.locationTerms !== true) {
          errors.locationTerms =
            'Du måste bekräfta att din fastighet inte ligger på en ö eller på militärt område';
        }
        if (extraData.ownershipTerms !== true) {
          errors.ownershipTerms =
            'Du måste bekräfta att du äger fastigheten eller har tillåtelse från fastighetsägaren att beställa tjänsten';
        }
        return errors;
      },
      confirmProductSelection: confirmDroneProductSelection,
      selectedProductSummationText: 'hektar',
      getSelectedProductSummationTotal: (products) =>
        products.reduce((acc, curr) => acc + Number(curr?.area ?? 0), 0)
    }),
    [stepKeys, confirmDroneProductSelection]
  );

  return (
    <CheckoutProvider
      totalSteps={stepKeys.length}
      config={config}
      initialProducts={initialProducts}
    >
      <DroneCheckoutInner
        stepKeys={stepKeys}
        isOpen={isOpen}
        onClose={onClose}
        openOptions={openOptions}
        onConsumedOpenOptions={onConsumedOpenOptions}
      />
    </CheckoutProvider>
  );
}
